import { Component, type ErrorInfo, type ReactNode } from 'react'
import { useRouteError, isRouteErrorResponse, useNavigate, useLocation } from 'react-router'

interface Props {
  children?: ReactNode
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info)
  }

  render() {
    if (this.state.error) {
      return <ErrorDisplay message={this.state.error.message} />
    }
    return this.props.children
  }
}

function ErrorDisplay({ message }: { message?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100dvh', flexDirection: 'column', gap: 12,
      background: '#0a0a0a',
    }}>
      <span style={{ color: '#f55', fontSize: 14, fontWeight: 600 }}>Something went wrong</span>
      {message && (
        <code style={{ color: '#444', fontSize: 11, maxWidth: 480, textAlign: 'center', wordBreak: 'break-all' }}>
          {message}
        </code>
      )}
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: 8, background: 'transparent', border: '1px solid #222',
          borderRadius: 5, padding: '6px 14px', color: '#555', fontSize: 12,
          cursor: 'pointer',
        }}
      >
        Reload
      </button>
    </div>
  )
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', gap: 0, textAlign: 'center', padding: '0 24px',
      }}>
        <div className="mono" style={{ fontSize: 96, fontWeight: 700, color: 'var(--line)', lineHeight: 1, letterSpacing: '-0.04em', marginBottom: 24 }}>
          404
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 600, marginBottom: 12 }}>Page not found.</h1>
        <p style={{ fontSize: 14, color: 'var(--fg-3)', maxWidth: 380, lineHeight: 1.6, marginBottom: 32 }}>
          <code className="mono" style={{ fontSize: 12, color: 'var(--fg-2)', background: 'var(--bg-2)', padding: '2px 6px', borderRadius: 4 }}>
            {pathname}
          </code>
          {' '}doesn't exist. If you're looking for a grid, the URL should include both a handle and a slug.
        </p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-ghost btn-mono" onClick={() => navigate(-1)}>← Go back</button>
          <button className="btn btn-primary" onClick={() => navigate('/')}>Home</button>
        </div>
      </div>
    )
  }

  let message = 'Unknown error'
  if (isRouteErrorResponse(error)) {
    message = `${error.status} ${error.statusText}`
  } else if (error instanceof Error) {
    message = error.message
  } else if (typeof error === 'string') {
    message = error
  }
  return <ErrorDisplay message={message} />
}
